import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StatusChip from "../../../../components/common/StatusChip";
import ChipList from "../../../../components/common/ChipList";
import { formatClientDate } from "../../utils/normalizeClient";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const ClientDetailsDrawer = ({
  open,
  client,
  onClose,
  onEdit,
  onUpdateRole,
  onRefreshSecret,
}) => {
  if (!client) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
      PaperProps={{
        sx: (theme) => {
          const topOffset = theme.spacing(8);
          return {
            width: { xs: "100%", sm: 440 },
            top: topOffset,
            height: `calc(100% - ${topOffset})`,
            maxHeight: `calc(100vh - ${topOffset})`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          };
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              Client Details
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: "break-word", mt: 0.25 }}
            >
              {client.name || client.clientId}
            </Typography>
          </Box>
          <IconButton aria-label="Close details" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 3,
          pt: 2,
          pb: 2.5,
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DetailItem label="Client ID" value={client.clientId} />
              </Grid>
              <Grid item xs={12}>
                <DetailItem label="Client Name" value={client.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={client.enabled ? "enabled" : "disabled"} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Allow user creation
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip
                    status={client.allowUserCreation ? "enabled" : "disabled"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Self user creation
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip
                    status={client.allowSelfUserCreation ? "enabled" : "disabled"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Login via OTP
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip
                    status={client.allowLoginViaOTP ? "enabled" : "disabled"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Created Date"
                  value={formatClientDate(client.creationDate || client.createdAt)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Updated Date"
                  value={formatClientDate(client.lastModifiedDate || client.updatedAt)}
                />
              </Grid>
              <Grid item xs={12}>
                <DetailItem label="Privacy Policy" value={client.privacyPolicy} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Access token expiry (sec)"
                  value={client.accessTokenExpiryInSecs}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Refresh token expiry (sec)"
                  value={
                    client.supportsRefreshToken
                      ? client.refreshTokenExpiryInSecs
                      : "—"
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Grant
            </Typography>
            <ChipList
              items={client.grantType}
              maxVisible={null}
              emptyLabel="No grant types"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Roles
            </Typography>
            <ChipList
              items={client.roles}
              maxVisible={null}
              emptyLabel="No roles assigned"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Redirect URIs
            </Typography>
            <ChipList
              items={client.redirectUri}
              maxVisible={null}
              emptyLabel="No redirect URIs"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack spacing={1.25}>
          {onEdit || onUpdateRole ? (
            <Stack direction="row" spacing={1.25}>
              {onEdit ? (
                <Button fullWidth variant="outlined" onClick={onEdit} sx={{ whiteSpace: "nowrap" }}>
                  Edit Client
                </Button>
              ) : null}
              {onUpdateRole ? (
                <Button fullWidth variant="outlined" onClick={onUpdateRole} sx={{ whiteSpace: "nowrap" }}>
                  Update Roles
                </Button>
              ) : null}
            </Stack>
          ) : null}
          {onRefreshSecret ? (
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={onRefreshSecret}
              sx={{ whiteSpace: "nowrap" }}
            >
              Refresh Client Secret
            </Button>
          ) : null}
          <Button fullWidth variant="text" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ClientDetailsDrawer;
